import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  useTheme,
  useMediaQuery,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Divider
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthService } from '../services/authService';
import headerLogo from '../img/headerlogo.png';
import MenuIcon from '@mui/icons-material/Menu';

function Header({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isAuthenticated, signOut } = useAuthService();
  
  // State for hamburger menu
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  // Function to handle navigation and close menu
  const handleNavigation = (path) => {
    navigate(path);
    handleMenuClose();
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/');
      // Force refresh of the page to ensure state is cleared
      window.location.reload();
    } catch (error) {
      console.error('Logout error:', error);
    }
    handleMenuClose();
  };

  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        backgroundColor: 'black', 
        boxShadow: isMobile ? '0px 2px 10px rgba(0, 0, 0, 0.5)' : 'none',
        zIndex: 1100,
        top: 0,
        left: 0,
        right: 0,
        width: '100%'
      }}
    >
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {/* Logo */}
        <Box
          component="img"
          src={headerLogo}
          alt="Screamify"
          sx={{
            height: { xs: '25px', sm: '30px' },
            cursor: 'pointer'
          }}
          onClick={() => navigate('/')}
        />
        
        {/* If there are children, render them */}
        {children && <Box sx={{ flexGrow: 1 }}>{children}</Box>}
        
        {/* Navigation Menu */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
          {/* Sign In Button - only show when not authenticated */}
          {!isAuthenticated && (
            <Button
              variant="contained"
              color="error"
              sx={{
                bgcolor: '#ff0000',
                '&:hover': {
                  bgcolor: '#cc0000'
                },
                minWidth: { xs: '80px', sm: '100px' },
                height: { xs: '32px', sm: '36px' },
                whiteSpace: 'nowrap',
                fontWeight: 700,
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                textTransform: 'none',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(255, 0, 0, 0.25)',
                WebkitTapHighlightColor: 'transparent',
                color: 'white',
                '&:active': {
                  bgcolor: '#990000',
                  transform: 'scale(0.98)'
                }
              }}
              onClick={() => navigate('/login?activeTab=0')}
            >
              Sign In
            </Button>
          )}
          
          {/* Hamburger Menu Button */}
          <IconButton
            size="large"
            edge="end"
            color="inherit"
            aria-label="menu"
            onClick={handleMenuOpen}
            sx={{ color: '#ffffff' }}
          >
            <MenuIcon />
          </IconButton>
          
          {/* Menu */}
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              sx: {
                backgroundColor: 'black',
                color: 'white',
                border: '1px solid rgba(255, 0, 0, 0.3)',
                '& .MuiMenuItem-root:hover': {
                  backgroundColor: 'rgba(255, 0, 0, 0.1)',
                },
                minWidth: '180px'
              },
            }}
          >
            {/* Navigation Items */}
            <MenuItem 
              onClick={() => handleNavigation('/')}
              sx={{ 
                fontWeight: location.pathname === '/' ? 700 : 400,
                color: location.pathname === '/' ? '#ff0000' : 'white'
              }}
            >
              Home
            </MenuItem>
            <MenuItem 
              onClick={() => handleNavigation('/blog')}
              sx={{ 
                fontWeight: location.pathname === '/blog' ? 700 : 400,
                color: location.pathname === '/blog' ? '#ff0000' : 'white'
              }}
            >
              Blog
            </MenuItem>
            {/* Community removed from hamburger when signed in per requirements */}
            
            <Divider sx={{ bgcolor: 'rgba(255, 0, 0, 0.3)', my: 1 }} />
            
            {/* Conditional items based on authentication */}
            {isAuthenticated ? (
              <>
                <MenuItem onClick={() => handleNavigation('/billing')}>
                  Billing
                </MenuItem>
                <Divider sx={{ bgcolor: 'rgba(255, 0, 0, 0.3)', my: 1 }} />
                <MenuItem 
                  onClick={handleLogout}
                  sx={{ color: '#ff0000' }}
                >
                  Log Out
                </MenuItem>
              </>
            ) : (
              <MenuItem 
                onClick={() => handleNavigation('/login?activeTab=0')}
                sx={{ color: '#ff0000', fontWeight: 500 }}
              >
                Sign In
              </MenuItem>
            )}
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}

export default Header; 